import {
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Group,
  Stack,
  Text,
  Timeline,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCircleDashed,
  IconFlag,
  IconLink,
} from '@tabler/icons-react';
import { Link, useParams } from 'react-router';
import { MetaPair } from '@/components/meta-pair';
import { PageHeader } from '@/components/page-header';
import { SectionHeading } from '@/components/section-heading';
import { SampleDataBanner } from '@/components/sample-data-banner';
import { FUNNEL_STEPS, type JourneyPhase } from '@/lib/analytics';
import { journeys } from '@/lib/fixtures/analytics';
import classes from './analytics-page.module.css';

const phaseColour: Record<JourneyPhase, string> = {
  anonymous: 'gray',
  adoption: 'magenta',
  account: 'brand',
};

const phaseLabel: Record<JourneyPhase, string> = {
  anonymous: 'Anonymous',
  adoption: 'Adoption',
  account: 'Account',
};

export function JourneyDetailPage() {
  const { journeyId } = useParams();
  const journey = journeys.find((entry) => entry.id === journeyId);

  if (!journey) {
    return (
      <Stack gap="lg" className={classes.page}>
        <PageHeader title="Journey not found" />
        <Alert color="gray" title="No such user">
          <Text size="sm">
            Nothing matches that id.{' '}
            <Anchor component={Link} to="/analytics/journeys">
              Back to journeys
            </Anchor>
            .
          </Text>
        </Alert>
      </Stack>
    );
  }

  const reached =
    FUNNEL_STEPS.find((step) => step.id === journey.reached)?.label ??
    journey.reached;
  const adoptedAt = journey.events.find((event) => event.phase === 'adoption');
  const anonymousCount = journey.events.filter(
    (event) => event.phase === 'anonymous',
  ).length;

  return (
    <Stack gap="xl" className={classes.page}>
      <SampleDataBanner />

      <Anchor component={Link} to="/analytics/journeys" size="sm">
        <Group gap={4} wrap="nowrap">
          <IconArrowLeft size={14} />
          Journeys
        </Group>
      </Anchor>

      <PageHeader
        title={journey.label}
        description={journey.headline}
        actions={<Code>{journey.anonymousId}</Code>}
      />

      <Card>
        <Group gap="xl" wrap="wrap">
          <MetaPair label="Language" value={journey.language} />
          <MetaPair label="Platform" value={journey.platform} />
          <MetaPair label="First run" value={journey.firstRunAt} />
          <MetaPair label="Reached" value={reached} />
          <MetaPair label="Events" value={String(journey.events.length)} />
        </Group>
      </Card>

      {adoptedAt ? (
        <Card className={classes.seam}>
          <Group gap="xs" wrap="nowrap" mb={4}>
            <IconLink size={16} className={classes.seamIcon} />
            <Text size="sm" fw={600}>
              Account seam
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {anonymousCount} events were recorded before this user had an
            account. Adoption at {adoptedAt.at} linked them to one rather than
            rewriting them, which is what lets the pre-account half of the
            journey stay readable.
          </Text>
        </Card>
      ) : (
        <Alert
          variant="light"
          color="gray"
          icon={<IconCircleDashed size={18} />}
          title="Still anonymous"
        >
          <Text size="sm">
            No account has adopted this user. Everything below is
            device-scoped, and a reinstall would start a new chain that nothing
            could join to this one.
          </Text>
        </Alert>
      )}

      <Card>
        <Stack gap="lg">
          <SectionHeading
            eyebrow="Timeline"
            title="Events"
            subtitle="In order, with the account seam marked where it falls."
            note="Events keep their original subject for good; adoption is recorded as a link rather than by rewriting history. That is what lets the pre-account half of a journey stay readable, including the case where somebody signs into an account that already has history."
          />

          <Timeline
            active={journey.events.length}
            bulletSize={22}
            lineWidth={2}
          >
            {journey.events.map((event, index) => {
              const phaseChanged =
                index === 0 || journey.events[index - 1]?.phase !== event.phase;

              return (
              <Timeline.Item
                key={event.id}
                color={phaseColour[event.phase]}
                bullet={event.milestone ? <IconFlag size={12} /> : undefined}
                title={
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" fw={600}>
                      {event.title}
                    </Text>
                    {event.milestone && (
                      <Badge variant="light" color="brand" size="xs">
                        Rung
                      </Badge>
                    )}
                    {phaseChanged && (
                      <Badge
                        variant="outline"
                        color={phaseColour[event.phase]}
                        size="xs"
                      >
                        {phaseLabel[event.phase]}
                      </Badge>
                    )}
                  </Group>
                }
              >
                {event.detail && (
                  <Text size="sm" c="dimmed">
                    {event.detail}
                  </Text>
                )}
                <Text size="xs" c="dimmed" mt={4}>
                  {event.at}
                </Text>
              </Timeline.Item>
              );
            })}
          </Timeline>
        </Stack>
      </Card>
    </Stack>
  );
}
